import type { IDropdownOption } from "@fluentui/react";
import { Dropdown, Stack, SpinButton } from "@fluentui/react";
import { useEffect, useState, type SyntheticEvent } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode from "./action-config-node";
import type { SetAndCheckStateVariableData } from "../../../api";
import { StateVariableAction } from "../../../api";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type SetAndCheckStateVariableNode = Node<
    InterfaceToRecord<SetAndCheckStateVariableData & ExtraNodeData>
>;

export default function SetAndCheckStateVariable(
    props: NodeProps<SetAndCheckStateVariableNode>,
): JSX.Element {
    const { t }: { t: TFunction } = useTranslation();

    const [action, setAction] = useState(props.data.action ?? StateVariableAction.TOGGLE);
    const [number, setNumber] = useState(props.data.variableIndex ?? 1);
    const updateNode = useUpdateNodeData<SetAndCheckStateVariableNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ variableIndex: number, action });
    }, [number, action, updateNode]);

    const dropdownOptionsStateVariableAction: IDropdownOption[] = Object.values(
        StateVariableAction,
    ).map((key, index) => ({
        key,
        text: t(`component.actionConfigNodes.action.${index}`),
    }));

    function onSpinButtonChange(
        _e: SyntheticEvent<HTMLElement, Event>,
        newValue?: string | undefined,
    ): void {
        if (!newValue) {
            return;
        }
        const newNumber = parseInt(newValue);
        setNumber(newNumber);
    }

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <Stack
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Dropdown
                    options={dropdownOptionsStateVariableAction}
                    selectedKey={action}
                    onChange={(_e, option) => setAction(option?.key as StateVariableAction)}
                    styles={{ dropdown: { width: 200 } }}
                />
                <SpinButton
                    className={"nodrag"}
                    value={number.toString()}
                    onChange={onSpinButtonChange}
                    min={1}
                    max={100}
                />
            </Stack>
        </ActionConfigNode>
    );
}
